import { ARTIFACT_MAX_RELATIVE_CHANGE, ARTIFACT_METHOD } from './constants';
import type { NnInterval } from './types';

const median = (values: number[]) => {
  if (values.length === 0) return undefined;
  const sorted = [...values].sort((left, right) => left - right);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

const interpolateRr = (previous: number, next: number) => (previous + next) / 2;

export interface ArtifactCorrectionResult {
  intervals: NnInterval[];
  totalBeats: number;
  correctedBeats: number;
  correctedBeatPercent: number;
  method: string;
}

export const correctArtifacts = (
  intervals: NnInterval[]
): ArtifactCorrectionResult => {
  if (intervals.length === 0) {
    return {
      intervals: [],
      totalBeats: 0,
      correctedBeats: 0,
      correctedBeatPercent: 0,
      method: ARTIFACT_METHOD,
    };
  }

  const working = intervals.map(item => ({ ...item, corrected: false }));
  let correctedBeats = 0;

  const markCorrected = (index: number, newRrMs: number) => {
    if (!working[index].corrected) {
      correctedBeats += 1;
    }
    working[index] = {
      ...working[index],
      rrMs: newRrMs,
      corrected: true,
    };
  };

  for (let index = 0; index < working.length; index += 1) {
    const current = working[index].rrMs;
    const windowStart = Math.max(0, index - 2);
    const windowEnd = Math.min(working.length, index + 3);
    const localValues = working
      .slice(windowStart, windowEnd)
      .map(item => item.rrMs);
    const localMedian = median(localValues) ?? current;

    let shouldCorrect = false;
    let replacement = current;

    if (index > 0) {
      const previous = working[index - 1].rrMs;
      const relativeChange = Math.abs(current - previous) / previous;
      if (relativeChange > ARTIFACT_MAX_RELATIVE_CHANGE) {
        shouldCorrect = true;
        replacement = interpolateRr(
          previous,
          working[index + 1]?.rrMs ?? current
        );
      }
    }

    const medianDeviation =
      localMedian === 0 ? 0 : Math.abs(current - localMedian) / localMedian;
    if (medianDeviation > ARTIFACT_MAX_RELATIVE_CHANGE) {
      shouldCorrect = true;
      replacement = localMedian;
    }

    if (shouldCorrect) {
      markCorrected(index, replacement);
    }
  }

  const totalBeats = working.length;
  const correctedBeatPercent =
    totalBeats === 0 ? 0 : (correctedBeats / totalBeats) * 100;

  return {
    intervals: working,
    totalBeats,
    correctedBeats,
    correctedBeatPercent,
    method: ARTIFACT_METHOD,
  };
};
